import { format, parse } from 'date-fns';
import { Chip, Divider, AvatarInput, Tooltip } from '@ds';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ConditionalRender } from '@ui';
import { getFullName } from '@/utils/helpers';
import { EmployeeComposed } from '@/api/apis/resources';
import { EMPLOYEE_ROLE_NAMES, roleNames } from '@/constants/user.constants';
import { TMDXFile } from '@/api/apis/files';
import { ProfileFormType } from '../types';
import { LOGO_IMAGE_TYPES } from '@/constants/file.constants';
import { OrganSection } from './OrganSection';

interface ProfileProps {
  data: EmployeeComposed;
  isReadOnly?: boolean;
}

export const UserBoard = ({ data, isReadOnly }: ProfileProps) => {
  const { setValue, control } = useFormContext<ProfileFormType>();
  const [profilePhoto] = useWatch({ control, name: ['profilePhoto'] });

  const userRoles = useMemo(() => {
    return data.roles?.map((r) => roleNames[r] ?? '').filter(Boolean) || [];
  }, [data.roles]);

  const isSpecialist = userRoles.includes(EMPLOYEE_ROLE_NAMES['clinical-specialist']);

  const isRetrievalSurgeon = userRoles.includes(EMPLOYEE_ROLE_NAMES['retrieval-surgeon']);

  const isClinicalLead = userRoles.includes(EMPLOYEE_ROLE_NAMES['clinical-lead']);

  const isScheduleManagerRs = userRoles.includes(EMPLOYEE_ROLE_NAMES['schedule-manager-rs']);

  const isOrganManagementSpecialistSpecializations = userRoles.includes(
    EMPLOYEE_ROLE_NAMES['organ-management-specialist'],
  );

  const isDateOfBirthFieldShown = isSpecialist || isRetrievalSurgeon;

  const isOrganFieldShown =
    (isSpecialist || isRetrievalSurgeon || isClinicalLead) && data.organType && data.organType.length > 0;

  const userFullName = getFullName(data?.firstName, data?.lastName);

  const onImageChange = (file: TMDXFile) => {
    setValue('photoFile', file);
  };

  return (
    <div className="flex flex-row justify-between ml-5">
      <div className="flex flex-col w-[291px] h-fit p-6 gap-3 rounded-lg bg-gray-100">
        <div className="flex flex-col items-center justify-between gap-1">
          <div className="mb-4">
            <AvatarInput
              src={profilePhoto}
              radius="xxl"
              disabled={isReadOnly}
              isBordered
              color="primary"
              size="xxl"
              name={userFullName}
              onFileChange={onImageChange}
              accept={LOGO_IMAGE_TYPES}
            />
          </div>
          <h1 className="text-hl text-center text-base-5 mb-1">{userFullName}</h1>
          {!!data?.position && <p className="text-m-r text-base-20 m-0">{data.position}</p>}
        </div>
        <Divider className="my-4" />
        <div className="flex flex-col gap-3">
          <div className="flex flex-col items-stretch gap-1">
            <p className="text-m-r text-base-20">Role</p>
            <div className="flex flex-wrap gap-2">
              {userRoles.map((role) => {
                if (role) {
                  return <Chip key={role}>{role}</Chip>;
                }
                return null;
              })}
            </div>
          </div>
          <OrganSection label="Organ" organs={data.organType} show={isOrganFieldShown} />
          <OrganSection
            label="Clinical Lead Organ"
            organs={data.phoneSupportOrganSpecializations}
            show={isClinicalLead}
          />
          <OrganSection
            label="Schedule Manager RS Organ"
            organs={data.rsScheduleManagerOrgans}
            show={isScheduleManagerRs}
          />
          <OrganSection
            label="OMS Organ"
            organs={data.organManagementSpecialistSpecializations}
            show={isOrganManagementSpecialistSpecializations}
          />
          <OrganSection
            label="CS Shadow"
            organs={data.shadowOrganSpecializations}
            show={isSpecialist}
            showBackground={false}
          />
          <OrganSection
            label="CS Proctor"
            organs={data.proctorOrganSpecializations}
            show={isSpecialist}
            showBackground={false}
          />
          <ConditionalRender show={isDateOfBirthFieldShown}>
            <div className="flex flex-col items-stretch gap-1">
              <p className="text-m-r text-base-20">Date of Birth</p>
              <p className="text-l-r text-base-5">
                {data?.dateOfBirth ? format(parse(data.dateOfBirth, 'yyyy-MM-dd', new Date()), "MMM do',' yyyy") : '-'}
              </p>
            </div>
          </ConditionalRender>
          <div className="flex flex-col gap-1 items-stretch">
            <p className="text-m-r text-base-20">Hire Date</p>
            <p className="text-l-r text-base-5">
              {data?.hireDate ? format(parse(data.hireDate, 'yyyy-MM-dd', new Date()), "MMM do',' yyyy") : '-'}
            </p>
          </div>
          <div className="flex flex-col gap-1 items-stretch">
            <p className="text-m-r text-base-20">Email</p>
            <Tooltip placement="top" showArrow content={data?.email}>
              <p className="text-l-r min-w-0 truncate">{data?.email}</p>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};
